import { EnvironmentId } from "@afar-research/contracts";
import { useAtomValue } from "@effect/atom-react";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";

import {
  getClientSettingsHydratedSnapshot,
  subscribeClientSettingsHydration,
} from "../clientSettingsStore";
import { useClientSettings } from "../hooks/useSettings";
import { usePageUnused } from "../hooks/usePageUnused";
import { readLocalApi } from "../localApi";
import {
  cloudWorkspaceIdsAtom,
  sessionCloudWorkspaceIdAtom,
  reconcileCloudWorkspaces,
  selectCloudWorkspaceForSession,
  settleCloudWorkspaces,
} from "./cloudWorkspaceRegistration";
import {
  addedConnectEnvironments,
  subscribeConnectEnvironmentsRefresh,
} from "./connectEnvironments";
import { checkConnectSnapshot, useConnectSnapshotCheckedOnUse } from "./connectSnapshot";
import { hostEnvironment } from "./hostEnvironment";
import { useAtomCommand } from "../state/use-atom-command";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { selectedCloudWorkspaceId } from "./cloudWorkspaceSelection.logic";
import {
  cloudClientAwayToReport,
  cloudWorkspaceConnectionWanted,
} from "./cloudWorkspaceIdle.logic";
import { useEnvironment } from "../state/environments";
import { serverEnvironment } from "../state/server";

/**
 * Restores this device's Cloud Workspace catalog on every page, and connects only the selected
 * workspace, which stays connected for the page's session until another workspace is selected. A
 * page unused for `CLOUD_UNUSED_WINDOW_MS` stays connected but tells the workspace it is away, so
 * the run can stop once its agent is idle too (`cloudWorkspaceConnectionWanted`). The Runtime Web Client has no local Environment from which to restore the catalog.
 */
export function CloudWorkspaceConnections(props: {
  readonly selectedEnvironmentId?: string | null;
}) {
  const bridge = readLocalApi()?.connect;
  const hydrated = useSyncExternalStore(
    subscribeClientSettingsHydration,
    getClientSettingsHydratedSnapshot,
  );
  // What this browser last heard from Connect, shown at once and checked when the page is used.
  const snapshot = useConnectSnapshotCheckedOnUse();
  const listing = useMemo(
    () =>
      snapshot === null
        ? null
        : {
            accountKey: snapshot.accountKey,
            workspaces: snapshot.workspaces.filter((workspace) => workspace.state !== "deleting"),
          },
    [snapshot],
  );
  const added = useClientSettings((settings) =>
    listing === null ? undefined : addedConnectEnvironments(settings, listing.accountKey),
  );
  const entries = useAtomValue(hostEnvironment.environmentEntriesValueAtom).entries;
  const sessionSelected = useAtomValue(sessionCloudWorkspaceIdAtom);
  const unused = usePageUnused();
  const setAway = useAtomCommand(serverEnvironment.setCloudClientAway, { reportFailure: false });
  const setEnabled = useAtomCommand(hostEnvironment.setEnabled, "Cloud Workspace selection");
  const catalog = useMemo(
    () =>
      new Map(
        [...entries].map(([environmentId, entry]) => [
          environmentId,
          { local: entry.target._tag === "PrimaryConnectionTarget" },
        ]),
      ),
    [entries],
  );

  useEffect(() => {
    if (!bridge?.cloudWorkspaces) {
      settleCloudWorkspaces();
      return;
    }
    const settle = (force: boolean) =>
      void checkConnectSnapshot({ force }).then(
        (checked) => {
          // Signed out: this device reaches no Cloud Workspace.
          if (checked === null)
            appAtomRegistry.set(cloudWorkspaceIdsAtom, { ids: new Set<string>() });
          settleCloudWorkspaces();
        },
        // Connect is unreachable; the app opens on what it already has rather than waiting on an
        // answer that may not come, and the next use asks again.
        () => settleCloudWorkspaces(),
      );
    settle(false);
    // A listing that was just read (an action's follow-up) is fresh already; only an old one asks.
    return subscribeConnectEnvironmentsRefresh(() => settle(false));
  }, [bridge]);

  useEffect(() => {
    if (listing === null) return;
    appAtomRegistry.set(cloudWorkspaceIdsAtom, {
      ids: new Set(listing.workspaces.map((workspace) => workspace.id)),
    });
  }, [listing]);

  useEffect(() => {
    // Reading what this device added before Client Settings arrive would read the defaults.
    if (!hydrated || listing === null) return;
    reconcileCloudWorkspaces({
      accountKey: listing.accountKey,
      listed: listing.workspaces,
      added,
      catalog,
    });
    settleCloudWorkspaces();
  }, [added, catalog, hydrated, listing]);

  // What this page may connect: the account's Cloud Workspaces and the machines this device reached
  // through an AFAR Network, which are selected and connected the same way (ADR-0146). Remembered
  // machines are read again whenever the catalog changes, as it does when one is connected.
  const reachable = useMemo(
    () =>
      listing === null
        ? null
        : [
            ...listing.workspaces.map((workspace) => workspace.id),
            ...(bridge?.networks?.rememberedMachines() ?? []).map((machine) => machine.id),
          ],
    // `entries` is a dependency for the machines it does not name: connecting one registers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bridge, entries, listing],
  );
  const selected = useMemo(
    () =>
      reachable === null
        ? null
        : selectedCloudWorkspaceId({
            workspaces: reachable.map((id) => ({ id })),
            routeEnvironmentId: props.selectedEnvironmentId ?? null,
            sessionSelected,
          }),
    [reachable, props.selectedEnvironmentId, sessionSelected],
  );
  const selectedEnvironmentId = selected === null ? null : EnvironmentId.make(selected);
  const selectedEnvironment = useEnvironment(selectedEnvironmentId);
  const connected = selectedEnvironment?.connection.phase === "ready";
  const awaySupported =
    selectedEnvironment?.serverConfig?.environment.capabilities.cloudClientAway === "away-v1";

  useEffect(() => {
    if (reachable === null) return;
    if (selected !== sessionSelected) selectCloudWorkspaceForSession(selected);
    for (const id of reachable) {
      const entry = entries.get(EnvironmentId.make(id));
      const isSelected = id === selected;
      const enabled = cloudWorkspaceConnectionWanted({
        selected: isSelected,
        unused,
        connected: isSelected && connected,
      });
      if (entry && entry.target._tag !== "PrimaryConnectionTarget" && entry.enabled !== enabled) {
        void setEnabled({ environmentId: EnvironmentId.make(id), enabled });
      }
    }
  }, [connected, entries, reachable, selected, sessionSelected, setEnabled, unused]);

  // What the current connection was last told; a new connection starts present.
  const reportedAway = useRef(false);
  useEffect(() => {
    if (!connected) reportedAway.current = false;
    const away = cloudClientAwayToReport({
      connected,
      supported: awaySupported,
      unused,
      reported: reportedAway.current,
    });
    if (away === null || selectedEnvironmentId === null) return;
    reportedAway.current = away;
    void setAway({ environmentId: selectedEnvironmentId, input: { away } });
  }, [awaySupported, connected, selectedEnvironmentId, setAway, unused]);

  return null;
}
